import { Component, Input, OnChanges, OnDestroy, SimpleChanges, TemplateRef, ViewChild } from '@angular/core'
import { MatDialog, MatDialogRef } from '@angular/material/dialog'
import { Editor, Toolbar, toHTML } from 'ngx-editor'
import { LeaderService } from 'src/app/leader-toolkit/services/leader.service'

export interface HospitalNote {
   id: number
   name: string
   description: string
   isHMVisible: boolean
   authorId: number | null
   author: string
   authorEmail: string
}

@Component({
   selector: 'vc-hospital-notes-card',
   templateUrl: './hospital-notes-card.component.html',
   styleUrl: './hospital-notes-card.component.scss',
   standalone: false,
})
export class HospitalNotesCardComponent implements OnChanges, OnDestroy {
   @Input() hospitalId = 0
   /** Viewer is a Hospital Manager (real or previewed): read-only, HM-visible notes only. */
   @Input() isHM = false
   /** View-only (e.g. "View as HM" preview): hides add / edit / delete for every role. */
   @Input() readOnly = false
   /** When false (HM dashboard page) the toggle/badge are hidden and notes are always saved as HM-visible. */
   @Input() showHmToggle = true
   /** Signed-in user; an HM may only edit / delete notes they authored. */
   @Input() currentUserName = ''
   @Input() currentUserId: number | null = null

   @ViewChild('noteDialogTpl') noteDialogTpl!: TemplateRef<any>
   @ViewChild('viewNoteDialogTpl') viewNoteDialogTpl!: TemplateRef<any>
   private noteDialogRef: MatDialogRef<any> | null = null
   private viewNoteDialogRef: MatDialogRef<any> | null = null

   editor: Editor = new Editor()
   toolbar: Toolbar = [
      ['bold', 'italic'],
      ['underline', 'strike'],
      ['code', 'blockquote'],
      ['ordered_list', 'bullet_list'],
      [{ heading: ['h1', 'h2', 'h3', 'h4', 'h5', 'h6'] }],
      ['link'],
      ['text_color', 'background_color'],
      ['align_left', 'align_center', 'align_right', 'align_justify'],
   ]

   hospitalNotes: HospitalNote[] = []
   notesLoading = false
   noteSaving = false
   editingNote: HospitalNote | null = null
   viewingNote: HospitalNote | null = null
   noteForm: { name: string; description: string; isHMVisible: boolean } = { name: '', description: '', isHMVisible: false }

   /** Add is allowed unless the card is in view-only mode. */
   get isReadOnly(): boolean {
      return this.readOnly
   }

   /** Every role can edit / delete only the notes they authored. */
   canEditNote(note: HospitalNote | null): boolean {
      if (this.readOnly || !note) return false
      return this.isOwnNote(note)
   }

   private isOwnNote(note: HospitalNote): boolean {
      if (this.currentUserId != null && note.authorId != null) {
         return Number(note.authorId) === Number(this.currentUserId)
      }
      const user = (this.currentUserName || '').trim().toLowerCase()
      if (!user) return false
      return [note.author, note.authorEmail].some((v) => (v || '').trim().toLowerCase() === user)
   }

   constructor(
      private leaderService: LeaderService,
      private dialog: MatDialog
   ) {}

   ngOnChanges(changes: SimpleChanges): void {
      if (changes['hospitalId'] || changes['isHM']) {
         this.loadHospitalNotes()
      }
   }

   ngOnDestroy(): void {
      this.editor.destroy()
   }

   loadHospitalNotes(): void {
      if (!this.hospitalId) {
         this.hospitalNotes = []
         return
      }
      this.notesLoading = true
      this.leaderService.getHospitalNotes(this.hospitalId).subscribe({
         next: (res: any) => {
            const data = res?.data ?? res
            const notes: HospitalNote[] = Array.isArray(data)
               ? data.map((n: any) => ({
                    id: n.noteId ?? n.id,
                    name: n.noteName ?? n.name,
                    description: n.description ?? '',
                    isHMVisible: !!(n.isHMVisible ?? n.isHmVisible),
                    authorId: n.createdById ?? n.createdByUserId ?? n.userId ?? null,
                    author: n.createdByName ?? n.createdBy ?? n.author ?? n.userName ?? n.displayName ?? '',
                    authorEmail: n.createdByEmail ?? n.email ?? '',
                 }))
               : []
            this.hospitalNotes = this.isHM ? notes.filter((n) => n.isHMVisible) : notes
            this.notesLoading = false
         },
         error: () => {
            this.hospitalNotes = []
            this.notesLoading = false
         },
      })
   }

   getNoteDescriptionPreview(description: string): string {
      if (!description) return ''
      const el = document.createElement('div')
      el.innerHTML = description
      return (el.textContent || el.innerText || '').trim()
   }

   openViewNoteModal(note: HospitalNote): void {
      this.viewingNote = note
      this.viewNoteDialogRef = this.dialog.open(this.viewNoteDialogTpl, {
         width: '560px',
         maxWidth: '95vw',
         disableClose: false,
         panelClass: 'note-dialog-panel',
      })
   }

   closeViewNoteModal(): void {
      this.viewNoteDialogRef?.close()
      this.viewNoteDialogRef = null
      this.viewingNote = null
   }

   editNoteFromView(): void {
      const note = this.viewingNote
      this.closeViewNoteModal()
      if (note) {
         this.openNoteModal(note)
      }
   }

   openNoteModal(note?: HospitalNote): void {
      if (note ? !this.canEditNote(note) : this.isReadOnly) return
      this.closeViewNoteModal()
      this.editingNote = note ?? null
      this.noteForm = note
         ? { name: note.name, description: note.description ?? '', isHMVisible: note.isHMVisible }
         : { name: '', description: '', isHMVisible: !this.showHmToggle }
      this.noteSaving = false
      this.noteDialogRef = this.dialog.open(this.noteDialogTpl, {
         width: '560px',
         maxWidth: '95vw',
         disableClose: false,
         panelClass: 'note-dialog-panel',
      })
   }

   closeNoteModal(): void {
      this.noteDialogRef?.close()
      this.noteDialogRef = null
      this.editingNote = null
      this.noteForm = { name: '', description: '', isHMVisible: false }
      this.noteSaving = false
   }

   private getNoteDescriptionHtml(): string {
      const textContent = this.editor.view?.state?.doc?.textContent?.trim()
      if (!textContent) {
         return ''
      }
      try {
         return toHTML(this.editor.view.state.doc.toJSON() as Record<string, unknown>, this.editor.schema)
      } catch {
         return typeof this.noteForm.description === 'string' ? this.noteForm.description : textContent
      }
   }

   saveNote(): void {
      const name = this.noteForm.name?.trim()
      if (!name || this.noteSaving || (this.editingNote ? !this.canEditNote(this.editingNote) : this.isReadOnly)) return
      const description = this.getNoteDescriptionHtml()
      const isHMVisible = this.showHmToggle ? this.noteForm.isHMVisible : true
      this.noteSaving = true

      if (this.editingNote) {
         this.leaderService.updateHospitalNote({ noteId: this.editingNote.id, noteName: name, description, isHMVisible }).subscribe({
            next: () => { this.loadHospitalNotes(); this.closeNoteModal() },
            error: () => { this.leaderService.showNotification('Failed to update note', true); this.noteSaving = false },
         })
      } else {
         this.leaderService.createHospitalNote({ practiceSKey: this.hospitalId, noteName: name, description, isHMVisible }).subscribe({
            next: () => { this.loadHospitalNotes(); this.closeNoteModal() },
            error: () => { this.leaderService.showNotification('Failed to create note', true); this.noteSaving = false },
         })
      }
   }

   deleteNote(note: HospitalNote): void {
      if (!this.canEditNote(note)) return
      this.leaderService.deleteHospitalNote(note.id).subscribe({
         next: () => this.loadHospitalNotes(),
         error: () => this.leaderService.showNotification('Failed to delete note', true),
      })
   }
}
